//@flow
import React, {Fragment, useEffect, useState} from 'react';
import makeStyles from '@material-ui/core/styles/makeStyles';
import PropTypes from 'prop-types';
import {Typography, Divider} from '@material-ui/core';
import {colors} from '../../../assets/styles/colors';
import AlertDueDateTier from '../../tiers/components/AlertDueDateTier/AlertDueDateTier';

import type {
  Store,
  User,
  Pagination as PaginationType,
} from '../../../entities';
import {usePage} from '../../page/utils';
import {CheckCircleOutlineTwoTone} from '@material-ui/icons';
import CustomButton from '../../../elements/CustomButton/CustomButton';
import Pagination from '@material-ui/lab/Pagination';
import noNotifications from '../../../assets/img/no-notifications.png';
import type {StoreNotification} from '../../../entities';
import {mapNotificationDetail} from '../utils';
import type {FetchNotificationsRequest} from '../duck';

import dayjs from 'dayjs';
import 'dayjs/locale/es';
import relativeTime from 'dayjs/plugin/relativeTime';
dayjs.locale('es');
dayjs.extend(relativeTime);

type Props = {
  store: Store,
  user: User,
  notifications: StoreNotification[],
  pagination: PaginationType,
  fetchNotifications: ({
    fetchNotificationsRequest: FetchNotificationsRequest,
  }) => void,
  markNotificationAsSeen: ({notificationId: string}) => void,
  markAllNotificationsAsSeen: () => void,
  notificationsResetState: () => void,
};

const useStyles = makeStyles(theme => ({
  navBackground: {
    [theme.breakpoints.down('sm')]: {
      background: 'white !important',
    },
  },
  root: {
    [theme.breakpoints.down('xs')]: {
      marginBottom: 100,
    },
  },
  title: {
    fontSize: '24px',
    fontWeight: 600,
    color: 'black',
  },
  content: {
    width: 600,
    maxWidth: 'calc(100% - 32px)',
    margin: 'auto',
    display: 'flex',
    flexDirection: 'column',
    marginTop: theme.spacing(5),
    padding: theme.spacing(2, 3),
    boxShadow:
      '0px 0px 1px rgba(181, 181, 181, 0.2), 0px 2px 8px rgba(209, 209, 209, 0.16)',
    backgroundColor: 'white',
    borderRadius: '12px',
    [theme.breakpoints.down('xs')]: {
      margin: theme.spacing(2),
    },
  },
  row: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  contentTitle: {
    fontSize: '1.125rem',
    fontWeight: 600,
  },
  markAllAsSeenButton: {
    backgroundColor: colors.grey200,
    borderRadius: '8px',
    whiteSpace: 'unset',
  },
  divider: {
    marginTop: theme.spacing(2),
    width: '100%',
    background: colors.grey300,
  },
  sectionHeader: {
    alignSelf: 'flex-start',
    margin: theme.spacing(2, 0, 1, 0),
    backgroundColor: colors.grey200,
    borderRadius: '4px',
    padding: '2px 6px',
    color: colors.grey700,
    fontSize: '0.75rem',
    fontWeight: 600,
    '&:first-letter': {
      textTransform: 'uppercase',
    },
  },
  notification: {
    margin: theme.spacing(0, -3, 0, -3),
    padding: theme.spacing(1, 3),
    borderRadius: 'unset',
  },
  notLinkNotification: {
    cursor: 'unset',
  },
  notificationContent: {
    width: '100%',
    textAlign: 'left',
    whiteSpace: 'normal',
  },
  text: {
    fontSize: '0.875rem',
    fontWeight: 500,
  },
  seenText: {
    fontWeight: 400,
  },
  time: {
    marginTop: 2,
    fontSize: '0.875rem',
    color: colors.purple3,
    fontWeight: 500,
  },
  seenTime: {
    color: colors.grey600,
    fontWeight: 400,
  },
  seenBadge: {
    margin: 'auto 8px',
    height: 8,
    width: 8,
    minWidth: 8,
    borderRadius: '4px',
    background: 'transparent',
  },
  seen: {
    background: colors.purple3,
  },
  noNotificationsImg: {
    alignSelf: 'center',
    marginTop: theme.spacing(7),
  },
  noNotificationsText: {
    margin: theme.spacing(3, 0, 7, 0),
    fontWeight: 500,
    color: colors.grey700,
    textAlign: 'center',
  },
  paginationArea: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    [theme.breakpoints.down('xs')]: {
      flexDirection: 'column',
    },
  },
  pagination: {
    margin: theme.spacing(2, 0),
    '& .Mui-selected': {
      backgroundColor: colors.lightPurple,
      color: colors.purple3,
    },
    '& .Mui-selected:hover': {
      backgroundColor: colors.lightPurple,
      // color: colors.purple3,
    },
  },
  sizeSelect: {
    margin: theme.spacing(2, 0),
    border: 'none',
    [theme.breakpoints.down('xs')]: {
      marginTop: 0,
    },
  },
}));

export default usePage<Props>(Notifications, 'Notifications History');

function Notifications(props: Props) {
  const {
    notifications,
    fetchNotifications,
    markNotificationAsSeen,
    markAllNotificationsAsSeen,
    pagination,
    notificationsResetState,
  } = props;

  const classes = useStyles();

  const [page, setPage] = useState(pagination.page + 1);
  const [size] = useState(pagination.size);
  const handlePageChange = (event, value) => {
    setPage(value);
  };

  useEffect(() => {
    if (!isNaN(page) && !isNaN(size)) {
      fetchNotifications({fetchNotificationsRequest: {size, page: page - 1}});
    }
  }, [fetchNotifications, page, size]);

  useEffect(() => {
    return () => {
      notificationsResetState();
    };
  }, [notificationsResetState]);

  const paginationArea = pagination && pagination.total > size && (
    <div className={classes.paginationArea}>
      <Pagination
        count={
          pagination &&
          (pagination.size >= pagination.total
            ? 1
            : Math.ceil(pagination.total / pagination.size))
        }
        page={page}
        className={classes.pagination}
        color="primary"
        onChange={handlePageChange}
      />
    </div>
  );

  return (
    <div className={classes.root}>
      <AlertDueDateTier />
      <div className={classes.content}>
        <div className={classes.row}>
          <Typography className={classes.contentTitle}>
            Historial de Notificaciones
          </Typography>
          <CustomButton
            variant="contained"
            color="default"
            className={classes.markAllAsSeenButton}
            startIcon={<CheckCircleOutlineTwoTone />}
            onClick={() => markAllNotificationsAsSeen()}
          >
            Marcar todas como leídas
          </CustomButton>
        </div>
        <Divider className={classes.divider} />
        {notifications && notifications.length ? (
          notifications.map((notification, i) => {
            const notificationDetail = mapNotificationDetail(notification);
            if (notificationDetail)
              return (
                <Fragment key={`notification-${i}`}>
                  {i === 0 ||
                  (notifications &&
                    notifications[i - 1] &&
                    notification &&
                    !dayjs(notification.createdAt).isSame(
                      notifications[i - 1].createdAt,
                      'day'
                    )) ? (
                    <>
                      {i > 0 && <Divider className={classes.divider} />}
                      <div className={classes.sectionHeader}>
                        {/* <div className={classes.sectionHeader}>
                            {`${dayjs(notification.createdAt).format(
                              'DD/MM/YYYY'
                            )} -`}
                          </div> */}
                        {`${dayjs(notification.createdAt).fromNow()}`}
                      </div>
                    </>
                  ) : null}
                  <CustomButton
                    color="default"
                    className={`${classes.notification} ${
                      !notificationDetail.linkType
                        ? classes.notLinkNotification
                        : ''
                    }`}
                    disableRipple={!notificationDetail.linkType}
                    to={
                      notificationDetail.linkType === 'internal'
                        ? notificationDetail.link
                        : undefined
                    }
                    href={
                      notificationDetail.linkType === 'external'
                        ? notificationDetail.link
                        : undefined
                    }
                    target={
                      notificationDetail.linkType === 'external'
                        ? '_blank'
                        : undefined
                    }
                    rel={
                      notificationDetail.linkType === 'external'
                        ? 'noopener noreferrer'
                        : undefined
                    }
                    onClick={
                      !notification.reviewed
                        ? () =>
                            markNotificationAsSeen({
                              notificationId: notification.id,
                            })
                        : undefined
                    }
                  >
                    <div className={classes.notificationContent}>
                      <Typography
                        className={`${classes.text} ${
                          notification.reviewed ? classes.seenText : ''
                        }`}
                      >
                        {notificationDetail.message}
                      </Typography>
                      <Typography
                        className={`${classes.time} ${
                          notification.reviewed ? classes.seenTime : ''
                        }`}
                      >
                        {dayjs(notification.createdAt).fromNow()}
                      </Typography>
                    </div>
                    <div
                      className={`${classes.seenBadge} ${
                        notification.reviewed ? '' : classes.seen
                      }`}
                    />
                  </CustomButton>
                </Fragment>
              );
            else return null;
          })
        ) : (
          <>
            <img
              className={classes.noNotificationsImg}
              src={noNotifications}
              alt="no notifications"
            />
            <Typography className={classes.noNotificationsText}>
              No tienes notificaciones
            </Typography>
          </>
        )}
      </div>
      {paginationArea}
    </div>
  );
}

Notifications.propTypes = {
  user: PropTypes.object,
  store: PropTypes.object,
  notifications: PropTypes.array,
  pagination: PropTypes.object,
  fetchNotifications: PropTypes.func.isRequired,
  markNotificationAsSeen: PropTypes.func.isRequired,
  markAllNotificationsAsSeen: PropTypes.func.isRequired,
  notificationsResetState: PropTypes.func.isRequired,
};
